import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import HeroSearch from "../components/HeroSearch";
import ReportCard from "../components/ReportCard";
import ReportUnlockGate, { UNLOCK_KEY } from "../components/ReportUnlockGate";
import SpecialistEnquiry from "../components/SpecialistEnquiry";
import TrustStatement from "../components/TrustStatement";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Loader2, ArrowRight, Briefcase, MapPin, ShieldCheck, Scale, MessageSquare } from "lucide-react";

function buildCheckoutHref(planId, q, industryLabel, postcode) {
  const p = new URLSearchParams({ plan: planId });
  if (q) p.set("business_name", q);
  if (industryLabel) p.set("industry_label", industryLabel);
  if (postcode) p.set("postcode", postcode);
  return `/checkout/details?${p.toString()}`;
}

function readStoredUnlock() {
  try {
    const raw = sessionStorage.getItem(UNLOCK_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && v.email ? v : null;
  } catch (_) {
    return null;
  }
}

export default function CheckResults() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const industry = params.get("industry") || "";
  const postcode = params.get("postcode") || "";
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // GA4 `free_name_search` - fires ONLY on a successful search completion,
  // NOT on button click, NOT on errors. The signature guards against firing
  // twice for the same search within a single component lifecycle (e.g. if
  // React StrictMode double-invokes the effect in dev, or if the same query
  // is retried after a transient error).
  const lastAnalyticsSignatureRef = useRef(null);

  // The report is lead-gated. On a fresh search we DON'T fetch the report; we
  // render <ReportUnlockGate/> instead. Once the visitor unlocks (name + email
  // + phone) we persist their details to sessionStorage, so any later search in
  // the same session auto-unlocks silently (still emailing them the new report).
  useEffect(() => {
    if (!q || q.length < 2) { setResult(null); return; }
    let cancelled = false;

    // Fire GA4 `free_name_search` once per unique (q, industry, postcode).
    const signature = `${q}|${industry || ""}|${postcode || ""}`;
    if (
      typeof window !== "undefined" &&
      typeof window.gtag === "function" &&
      lastAnalyticsSignatureRef.current !== signature
    ) {
      lastAnalyticsSignatureRef.current = signature;
      window.gtag("event", "free_name_search", {
        search_term: q,
        page_location: window.location.href,
      });
    }

    const stored = readStoredUnlock();
    if (!stored) {
      // Not unlocked yet this session - show the gate.
      setResult(null);
      setError("");
      setLoading(false);
      return () => { cancelled = true; };
    }

    // Already unlocked this session - auto-unlock silently with stored details.
    const run = async () => {
      setLoading(true);
      setError("");
      setResult(null);
      try {
        const r = await api.post("/name-check/unlock", {
          name: stored.name,
          email: stored.email,
          phone: stored.phone,
          query: q,
          industry: industry || null,
          postcode: postcode || null,
        });
        if (cancelled) return;
        setResult(r.data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not run the check. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [q, industry, postcode]);

  const industryLabel = result?.industry_label || "";
  const annualHref = buildCheckoutHref("annual", q, industryLabel, postcode);
  const monthlyHref = buildCheckoutHref("monthly", q, industryLabel, postcode);

  const showGate = Boolean(q) && !loading && !error && !result;

  // Per-query SEO: title carries the searched name so individual reports can
  // surface in branded / long-tail Google searches.
  useSeo({
    title: q
      ? `Risk report for “${q}” - UK Business Name Protection`
      : "Free UK Business Name Check - Risk Report",
    description: q
      ? `Live risk report for "${q}" across Companies House, domains and socials. See how protectable your UK business name is in under a minute.`
      : "Free 30-second UK business name check across Companies House, domains and socials. Clear risk score and an actionable report.",
    // Don't let Google index the individual /check?q=... permutations - there
    // are effectively unlimited variants; the homepage is the canonical entry.
    noindex: Boolean(q),
  });

  return (
    <main className="py-12 md:py-16 lg:py-20 xl:py-24" data-testid="check-results-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
        {/* Title + search bar */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="bnp-chip bnp-chip-teal">Free name check, step 1 of 2</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#04273d] mt-3 font-bold tracking-tight">
            Risk report for <span className="text-[#447694]">&ldquo;{q || "your business name"}&rdquo;</span>
          </h1>
          {(result?.industry_label || result?.postcode_area) && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-3" data-testid="check-context-chips">
              {result?.industry_label && (
                <span className="bnp-chip bnp-chip-light flex items-center gap-1.5" data-testid="check-industry-chip">
                  <Briefcase size={12} /> {result.industry_label}
                </span>
              )}
              {result?.postcode_area && (
                <span className="bnp-chip bnp-chip-light flex items-center gap-1.5" data-testid="check-postcode-chip">
                  <MapPin size={12} /> Postcode area {result.postcode_area}
                </span>
              )}
            </div>
          )}
          <div className="mt-6">
            <HeroSearch
              inline
              defaultName={q}
              defaultIndustry={industry}
              defaultPostcode={postcode}
            />
          </div>
        </div>

        {/* Report area */}
        <div className="mt-10">
          {loading && (
            <div className="bnp-card p-10 flex items-center justify-center gap-3 text-[#447694]" data-testid="check-loading-state">
              <Loader2 size={20} className="animate-spin text-[#71edc9]" />
              <span className="font-mono-brand text-sm uppercase tracking-wider">Running structured analysis</span>
            </div>
          )}
          {error && (
            <div className="bnp-card p-6 text-red-700" data-testid="check-error">{error}</div>
          )}
          {showGate && (
            <ReportUnlockGate
              query={q}
              industry={industry}
              postcode={postcode}
              onUnlocked={(data) => setResult(data)}
            />
          )}
          {!loading && !error && result && (
            <ReportCard
              result={result}
              checkoutHref={annualHref}
              query={q}
              industry={industry}
              postcode={postcode}
            />
          )}
          {!q && !loading && (
            <div className="bnp-card p-10 text-center text-[#447694]" data-testid="check-empty-state">
              <p>Enter a business name above to generate your free risk report.</p>
            </div>
          )}
        </div>

        {/* Final big CTA - switches to "Speak to a specialist" when the name is on
            the NBR register OR the computed risk is "Critical". */}
        {result && (() => {
          const needsSpecialist = result.nbr_available === false || result.risk_label === "Critical";
          if (needsSpecialist) {
            return (
              <section className="mt-16 pt-12 border-t border-[#9ac7f7]/25" data-testid="check-final-cta-specialist">
                <div className="text-center max-w-2xl mx-auto">
                  <MessageSquare size={36} className="text-amber-500 mx-auto" />
                  <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
                    Not sure whether this name is still viable?
                  </h2>
                  <p className="text-[#447694] mt-4 leading-relaxed">
                    A high-risk result doesn&rsquo;t always mean you need to start again.
                  </p>
                  <p className="text-[#447694] mt-3 leading-relaxed">
                    In some cases, there may be practical ways to reduce risk or strengthen your position. In others, choosing a different name could save significant expense later.
                  </p>
                  <p className="text-[#04273d] mt-3 leading-relaxed font-semibold">
                    That&rsquo;s why we recommend a quick review with one of our specialists before you proceed.
                  </p>
                  <div className="mt-8 max-w-xl mx-auto">
                    <SpecialistEnquiry
                      businessName={result.query}
                      matchedName={result.nbr_protected_match}
                      buttonClass="btn-primary justify-center text-base py-3.5 w-full"
                      buttonLabel="Discuss My Options"
                      testid="check-final-cta-specialist-button"
                    />
                  </div>
                  <p className="text-[12px] text-[#447694] mt-4">Free enquiry. No obligation. UK-based team.</p>
                  <TrustStatement className="mt-5" />
                </div>

                <div className="mt-10 max-w-3xl mx-auto bg-[#04273d] text-white rounded-3xl p-6 md:p-7 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/20 flex items-center justify-center shrink-0">
                    <Scale size={22} className="text-[#71edc9]" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-semibold">Why a specialist review first?</p>
                    <p className="text-sm text-[#9ac7f7] mt-1">When several signals overlap, the right next step often is <em>not</em> a standard plan. A five-minute conversation usually saves time, money and rework later.</p>
                  </div>
                </div>
              </section>
            );
          }
          return (
            <section className="mt-16 pt-12 border-t border-[#9ac7f7]/25" data-testid="check-final-cta">
              <div className="text-center max-w-2xl mx-auto">
                <ShieldCheck size={36} className="text-[#71edc9] mx-auto" />
                <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
                  Good news, &ldquo;{q}&rdquo; looks suitable for protection
                </h2>
                <p className="text-[#447694] mt-3">
                  Our checks found no significant conflicts that would normally prevent you from protecting this name.
                </p>
                <p className="text-[#04273d] mt-3 font-semibold">
                  Create your record of use, access UK-based support, and benefit from up to <strong>£10,000</strong> of passing-off assistance.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
                  <Link to={monthlyHref} className="btn-secondary justify-center text-base py-3.5" data-testid="check-final-cta-monthly">
                    Protect monthly &middot; £15/mo
                  </Link>
                  <Link to={annualHref} className="btn-primary justify-center text-base py-3.5" data-testid="check-final-cta-annual">
                    Protect My Name <ArrowRight size={14} />
                  </Link>
                </div>
                <p className="text-[12px] text-[#447694] mt-4">From £15 per month or £155.40 per annum including VAT.</p>
                <TrustStatement className="mt-5" />
              </div>

              <div className="mt-10 max-w-3xl mx-auto bg-[#04273d] text-white rounded-3xl p-6 md:p-7 flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/20 flex items-center justify-center shrink-0">
                  <Scale size={22} className="text-[#71edc9]" />
                </div>
                <div>
                  <p className="font-display text-lg font-semibold">Backed by up to £10,000 passing-off legal assistance</p>
                  <p className="text-sm text-[#9ac7f7] mt-1">If a competitor copies your name, you have real legal backing. Included on both plans.</p>
                </div>
              </div>
            </section>
          );
        })()}

        {/* Conversion nudge - once a report is on screen, invite the visitor to
            check another name. They're already unlocked this session, so a new
            search reveals the next report instantly (no gate). */}
        {result && (
          <section className="mt-14 max-w-3xl mx-auto" data-testid="check-another-nudge">
            <div className="bnp-card bg-[#f7f7f7]/60 border-[#9ac7f7]/40 p-6 md:p-7 text-center">
              <span className="bnp-chip bnp-chip-teal">Got another name in mind?</span>
              <h3 className="font-display text-xl md:text-2xl text-[#04273d] mt-3 font-bold tracking-tight">
                Check another business name
              </h3>
              <p className="text-sm text-[#447694] mt-1.5 max-w-lg mx-auto">
                Comparing a few options? Run another free check, your details are already saved, so the next report is instant.
              </p>
              <div className="mt-5">
                <HeroSearch inline />
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
